import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import TableCell from '../../components/TableCell';
import { formatDate } from '../../resources/functions';
import axios from '../../resources/axios';

interface UserSubscription {
  _id: string;
  subscriptionPlan: {
    _id: string;
    name: string;
    price: number;
    duration: number;
  };
  city: {
    _id: string;
    cityName: string;
  };
  createdAt: string;
  expiresAt: string;
  status: string;
  expired: boolean;
}

interface Props {
  userId: string;
}

const UserSubscriptionsContent = ({ userId }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [userSubscriptions, setUserSubscriptions] = useState<UserSubscription[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUserSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserSubscriptions = async () => {
    setLoading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('STORE_USER_INFO') || '{}');
      const token = userInfo.token;

      const response = await axios.get('/subscription/admin/user_subscriptions', {
        headers: {
          'x-access-token': token
        },
        params: {
          userId: userId,
          limit: 1000,
          page: 1
        }
      });

      if (response.data?.data?.subscriptions) {
        setUserSubscriptions(response.data.data.subscriptions);
      }
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: string, expired: boolean) => {
    if (expired || status === 'expired') {
      return { label: t('userModal.statusExpired'), color: 'default' as const };
    }
    if (status === 'cancelled') {
      return { label: t('userModal.statusCancelled'), color: 'error' as const };
    }
    return { label: t('userModal.statusActive'), color: 'success' as const };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userSubscriptions.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('userModal.noSubscriptions')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>{t('userModal.colSubscriptionPlan')}</TableCell>
              <TableCell>{t('userModal.colCity')}</TableCell>
              <TableCell>{t('userModal.colStartDate')}</TableCell>
              <TableCell>{t('userModal.colEndDate')}</TableCell>
              <TableCell>{t('userModal.colDuration')}</TableCell>
              <TableCell>{t('userModal.colStatus')}</TableCell>
              <TableCell>{t('userModal.colPrice')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userSubscriptions.map((subscription, index) => {
              const statusConfig = getStatusConfig(subscription.status, subscription.expired);
              
              return (
                <TableRow hover key={subscription._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: theme.palette.mode === 'light' ? '#E3F2FD' : 'rgba(33, 150, 243, 0.2)',
                          color: '#2196F3',
                          fontSize: 14,
                          fontWeight: 600
                        }}
                      >
                        {index + 1}
                      </Avatar>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {subscription.subscriptionPlan.name}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {subscription.city.cityName}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(subscription.createdAt, { time: true })}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(subscription.expiresAt, { time: true })}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {t('userModal.daysValue', { count: subscription.subscriptionPlan.duration })}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={statusConfig.label}
                      color={statusConfig.color}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {subscription.subscriptionPlan.price} ₴
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserSubscriptionsContent;
